'use strict';
// @ts-expect-error added to window here
window.sre = (function () {
    /** Directive attributes used by the engine.
     * These are attributes with special behavior. Any standard attribute can be made reactive with the `x-` prefix.
     */
    const DIRECTIVES = {
        FOR: 'x-for',
        IF: 'x-if',
        ELSE: 'x-else',
        SHOW: 'x-show',
        TEXT: 'x-text',
        CLASS: 'x-class',
    };
    /** property-to-nodes mapping. */
    const mapping = new Map();
    /**
     * Map that stores scope objects associated with unique scope IDs.
     *
     * Each scope is indexed by a unique string (scopeId) and holds its associated state,
     * including list items, instance nodes, iterator variable names, and more.
     * This is used to track and manage context for each loop or template scope in the reactive system.
     *
     * @type {Map<string, ScopeObject>}
     */
    const scopes = new Map();
    const eventMapping = new Map();
    /** Associates event handling metadata to a DOM node (key) */
    const domStates = new WeakMap();
    // associates DOM nodes to the comments that replaces them when not inserted via x-if/x-else
    const ifMap = new WeakMap();
    // expression to Function object mapping.
    // for event handler expressions, the value is an object with the fn property (the Function object)
    const fnMap = new Map();
    // watchers map: key is prop to watch, value is an array of functions
    // when a property is updated, all related functions are executed.
    const watchers = new Map();
    /** Property-matching regex */
    let propMatcher;
    /** Regex to parse for-loop directives */
    let forloopRx;
    // map [expression group] => [{nd,rule}]
    // stores nodes to render at init, without waiting for a state change.
    let prerenderMap = new Map();
    /** Events SRE looks for and binds listeners for */
    const SUPPORTED_EVENTS = ['click', 'keypress', 'slider-select'];
    let app;
    let logger;
    /** ***************************************
     * entry point
     *****************************************/
    async function start(obj, _logger) {
        performance.mark('sre-start');
        app = obj;
        const props = Object.keys(app._store); // prop names to observe
        logger = _logger || console;
        // match occurrences of all properties of the store proxy object
        const propRxDefinition = `(?:^|[^\\.])\\bstore\\.(${props.join('|')})\\b`;
        propMatcher = new RegExp(propRxDefinition, 'g');
        forloopRx = /^\((?<item>[a-zA-Z_]\w*)(?:, *(?<iter>[a-zA-Z_]\w*))?\) *of +(?<list>.+)$/;
        parseNode(document, null, null);
        prerender();
        // @ts-expect-error not used after parsing
        propMatcher = forloopRx = undefined; // clean regexp objects after use
        app.store = new Proxy(app._store, handler);
        performance.measure('sre-end', 'sre-start');
        logger.log('sre-init', {
            duration: Math.round(performance.getEntriesByName('sre-end', 'measure')[0].duration),
        });
        return app;
    }
    /**
     * Adds the provided functions to the watchers mapping for each property.
     * When a property is updated, all its registered watcher functions will be executed.
     * @param obj - Record mapping property names to their watcher functions
     */
    function watch(obj) {
        Object.entries(obj).forEach(([prop, fn]) => {
            const w = watchers.get(prop);
            if (w)
                w.push(fn);
            else
                watchers.set(prop, [fn]);
        });
    }
    /** Forces initial rendering of x-if/else directives that exist outside of templates */
    function prerender() {
        if (prerenderMap.size > 0) {
            performance.mark('init-if-start');
            const entries = prerenderMap.entries();
            let entry = entries.next();
            while (!entry.done) {
                const [grp, nds] = entry.value;
                const fn = fnMap.get(grp.expr);
                const res = fn.call(null, app.data, app._store);
                for (let i = 0; i < nds.length; i++) {
                    updateNode(nds[i].nd, nds[i].rule, res);
                }
                entry = entries.next();
            }
            performance.measure('init-if', 'init-if-start');
            logger.log('prerender', { duration: Math.round(performance.getEntriesByName('init-if', 'measure')[0].duration) });
        }
        // @ts-expect-error Not used after prerender
        prerenderMap = null; // get rid of the object and make it available for GC
    }
    // Analyzes DOM element. Sends to mapNode (initial parsing) or mapScopedNode (parsing templated content)
    function parseNode(root, scopeId, context) {
        const isDoc = root === document;
        if (isDoc) {
            // nested for loops are not supported
            for (const nd of root.querySelectorAll(`template[${DIRECTIVES.FOR}]`)) {
                mapNode(nd, DIRECTIVES.FOR, scopeId, context);
            }
        }
        const serialRoot = isDoc ? root.body : root;
        const matches = serialRoot.outerHTML.match(/\b(x-(?!for)[a-z_][\w-]+)/gi);
        if (matches) {
            const dynAttrs = [...new Set(matches)]; // remove duplicates
            for (let i = 0; i < dynAttrs.length; i++) {
                // process root node if it has the attribute (querySelectorAll cannot return called node itself). For non-document root only.
                if (!isDoc && root.hasAttribute(dynAttrs[i]))
                    mapNode(root, dynAttrs[i], scopeId, context);
                // process child nodes
                for (const nd of root.querySelectorAll(`[${dynAttrs[i]}]`)) {
                    mapNode(nd, dynAttrs[i], scopeId, context);
                }
            }
        }
        parseEvents(serialRoot, context);
        if (isDoc)
            bindEvents(root.body, null);
    }
    /**
     * Extracts argument names from a function call in an event handler expression.
     * Only extracts valid variable names, ignoring literals like strings, numbers,
     * expressions, nested function calls, etc.
     * @param expr The event handler expression to parse
     * @returns Array of argument names as strings
     */
    function getEventArgs(expr) {
        const start = expr.indexOf('('), end = expr.lastIndexOf(')'), str = expr.substring(start + 1, end), rx = /^\s*[a-zA-Z_\$][\w\.]*\s*$/; // eslint-disable-line no-useless-escape
        const elems = str.split(',');
        const args = [];
        for (let i = 0; i < elems.length; i++) {
            if (rx.test(elems[i]))
                args.push(elems[i].trim());
        }
        return args;
    }
    function parseEvents(nd, context) {
        for (let i = 0; i < SUPPORTED_EVENTS.length; i++) {
            const evt = '@' + SUPPORTED_EVENTS[i];
            if (nd.hasAttribute(evt)) {
                const expr = nd.getAttribute(evt);
                const args = getEventArgs(expr);
                try {
                    if (!fnMap.has(expr)) {
                        const o = { fn: Function(...args, `"use strict"; return (${expr});`) };
                        fnMap.set(expr, o);
                    }
                    const evtMapping = {
                        args: args.slice(1),
                    };
                    if (!eventMapping.has(expr))
                        eventMapping.set(expr, evtMapping);
                }
                catch (ex) {
                    logger.error(`${evt}="${expr}"`, ex);
                }
            }
        }
        for (let i = 0; i < nd.childElementCount; i++) {
            parseEvents(nd.children[i], context);
        }
    }
    /**
     * Creates and stores a scoped function for the given expression if it is not already cached.
     *
     * This helper dynamically generates a new `Function` instance from the provided expression and context,
     * saving it to the `fnMap` for later use. The resulting function is parameterized to accept the base
     * `data` and `store` objects, as well as any additional scope context variables supplied.
     *
     * @param expr - The expression string to convert into a function. If null, no action is taken.
     * @param scopeId - Optional identifier to indicate scoped usage. If null, no function is stored.
     * @param context - Optional array of variable names (iteration/context variables) to be passed as additional function parameters.
     */
    function createScopedFunction(expr, scopeId, context) {
        if (scopeId && expr && !fnMap.has(expr)) {
            let args = ['data', 'store'];
            if (context != null)
                args = args.concat(context);
            fnMap.set(expr, Function(...args, `"use strict"; return (${expr});`));
        }
    }
    /**
     * Checks directive value on node and adds the corresponding mapping to property and expression
     * @param nd - The HTML element to map
     * @param directive - The directive to check (e.g. 'x-for', 'x-if')
     * @param scopeId - Optional scope identifier for scoped expressions
     * @param context - Optional scope context containing iteration variables
     */
    function mapNode(nd, directive, scopeId, context) {
        let expr = nd.getAttribute(directive);
        try {
            createScopedFunction(expr, scopeId, context);
        }
        catch (ex) {
            logger.error(`${directive}="${expr}"`, ex);
        }
        try {
            // for loop: configure scope for later rendering
            if (directive === DIRECTIVES.FOR) {
                scopeId = Math.random().toString(36).substring(2);
                scopes.set(scopeId, {
                    items: [],
                    nodes: new Map(), // map templated nodes & their instances
                    iterName: '',
                    itemName: '',
                });
                nd.dataset.scope = scopeId;
            }
            // template node (i.e. grouped x-if/x-show): need to parse its content
            else if (nd.tagName === 'TEMPLATE') {
                renderTemplate(nd, scopeId, context);
                return;
            }
            if (directive === DIRECTIVES.ELSE) {
                expr = nd.previousElementSibling.getAttribute(DIRECTIVES.IF);
            }
            if (expr != null) {
                // keep track of found properties,
                // to add them only once if they're referenced multiple times.
                const propsInExpr = new Set();
                const matches = expr.matchAll(propMatcher);
                for (const m of matches) {
                    if (m && !propsInExpr.has(m[1])) {
                        /** Reactive property on the player.store object. */
                        const prop = m[1];
                        propsInExpr.add(prop);
                        const elem = { nd, rule: directive };
                        const data = mapping.get(prop);
                        let exprGrp;
                        // There is mapping already defined for this property
                        if (data) {
                            // look for an existing expression group for the current expression
                            exprGrp = data.find(x => x.expr === expr);
                            if (exprGrp) {
                                if (directive !== DIRECTIVES.FOR) {
                                    // add NodeRuleTuple to existing expression group
                                    exprGrp.nds.push(elem);
                                }
                                else {
                                    // A scope already exists for this loop expression, let's not create another one.
                                    // Delete new scope we just created and refresh the scopeId.
                                    scopes.delete(scopeId);
                                    scopeId = exprGrp.scopeId;
                                    nd.dataset.scope = scopeId;
                                    // If a scope is found, the current node will not use buildExpressionGroup,
                                    // and the node's children will not be parsed.
                                    // No need to call buildExpressionGroup again, we just want to parse the children.
                                    for (let i = 0; i < nd.content.childElementCount; i++) {
                                        parseNode(nd.content.children[i], scopeId, [
                                            exprGrp.itemName,
                                            exprGrp.iterName
                                        ]);
                                    }
                                    ;
                                    exprGrp.tmpls.push(nd);
                                }
                            }
                            else {
                                // create new expression group
                                exprGrp = buildExpressionGroup(expr, elem, scopeId, context);
                                if (exprGrp)
                                    data.push(exprGrp);
                            }
                        }
                        else {
                            // create new mapping object
                            exprGrp = buildExpressionGroup(expr, elem, scopeId, context);
                            if (exprGrp)
                                mapping.set(prop, [exprGrp]); // update mapping with new node to watch
                        }
                        // mark if/elses for pre-rendering
                        if (exprGrp && !scopeId
                            && (directive === DIRECTIVES.IF || directive === DIRECTIVES.ELSE)) {
                            const nds = prerenderMap.get(exprGrp);
                            if (nds)
                                nds.push(elem);
                            else
                                prerenderMap.set(exprGrp, [elem]);
                        }
                    }
                }
            }
            // if node is not inside a scope, remove directive attribute to lighten the DOM.
            // Do not remove for x-if directives that have a x-else sibling, as it will be needed.
            // For scoped nodes, directives are removed when rendered.
            if (!scopeId && (directive != DIRECTIVES.IF
                || !nd.nextElementSibling
                || !nd.nextElementSibling.hasAttribute(DIRECTIVES.ELSE))) {
                nd.removeAttribute(directive);
            }
        }
        catch (ex) {
            logger.error('mapNode error', ex);
        }
    }
    /**
     * Builds a LoopExpressionGroup for a scoped (x-for directive) expression group.
     *
     * This function parses the for-loop match and the associated template element,
     * sets up the local scope with iteration variables, and recursively parses all children
     * within the template's content with new scope information.
     *
     * @param forMatch - The result of a RegExp match for the x-for directive. Should include named groups for item, iter, and list.
     * @param expr - The full JavaScript expression to evaluate in the x-for directive.
     * @param elem - The NodeRuleTuple for this template/loop, containing the DOM node and the rule ('x-for').
     * @param scopeId - The unique identifier for this local iteration scope.
     * @returns The constructed LoopExpressionGroup, which tracks the template, iteration variable names, list path, and scopeId.
     */
    function buildScopedExpressionGroup(forMatch, expr, elem, scopeId) {
        const grp = forMatch.groups;
        const iter = grp.iter || 'i', itemName = grp.item;
        const nd = elem.nd;
        // parse all template's children
        for (let i = 0; i < nd.content.childElementCount; i++) {
            parseNode(nd.content.children[i], scopeId, [itemName, iter]);
        }
        const localScope = scopes.get(scopeId);
        localScope.iterName = iter;
        localScope.itemName = itemName;
        return {
            expr,
            tmpls: [nd],
            listPath: forMatch.groups.list.split('.'),
            itemName: itemName,
            iterName: iter,
            scopeId,
        };
    }
    /**
     * Builds a base expression group for a standard (non-loop) directive.
     *
     * If the provided expression is not already cached in `fnMap`, this function generates
     * a new `Function` instance representing the expression, which can be parameterized
     * with the application's data and store, along with any additional contextual scope variables.
     *
     * @param expr - The JavaScript expression to evaluate.
     * @param elem - The node and rule tuple ({ nd, rule }) that uses the expression.
     * @param scopeId - Optional scope identifier if the expression is used within a loop or scoped context.
     * @param context - Optional array specifying any additional context variables provided by a loop or template scope.
     * @returns The constructed BaseExpressionGroup object for reference and reactive mapping.
     */
    function buildBaseExpressionGroup(expr, elem, scopeId, context) {
        if (!fnMap.has(expr)) {
            fnMap.set(expr, Function('data', 'store', ...(context || []), `"use strict"; return (${expr});`));
        }
        return {
            expr,
            nds: [elem],
            scopeId,
        };
    }
    function buildExpressionGroup(expr, elem, scopeId, context) {
        const forMatch = expr.match(forloopRx);
        // Expression matches x-for directive, build scoped expression group
        if (forMatch)
            return buildScopedExpressionGroup(forMatch, expr, elem, scopeId);
        // Otherwise map as any other directives
        try {
            return buildBaseExpressionGroup(expr, elem, scopeId, context);
        }
        catch (ex) {
            logger.error(`${elem.rule}="${expr}"`, ex);
        }
    }
    function processLoop(exprGrp, oldLength) {
        let list = app;
        for (let i = 0; i < exprGrp.listPath.length; i++)
            list = list[exprGrp.listPath[i]];
        if (list == null)
            return;
        for (let tplIdx = 0; tplIdx < exprGrp.tmpls.length; tplIdx++) {
            const localScope = scopes.get(exprGrp.scopeId);
            localScope.items = list;
            // templated nodes are added to a fragment, which is then inserted in the document.
            // Only one DOM insertion, better for perfs.
            const sourceNd = exprGrp.tmpls[tplIdx].content.firstElementChild;
            // previous sibling of the current node.
            // First iteration: template node; following iterations: node created by previous iteration
            let previousSibling = exprGrp.tmpls[tplIdx];
            for (let iter = 0; iter < list.length; iter++) {
                // instanciate template
                const nd = sourceNd.cloneNode(true);
                renderNode(nd, exprGrp.scopeId, [list[iter], iter], sourceNd, list.length);
                bindEvents(nd, {
                    [exprGrp.itemName]: list[iter],
                    [exprGrp.iterName]: iter,
                });
                if (exprGrp.scopeId)
                    nd.classList.add(exprGrp.scopeId);
                // replaces existing node if possible, or inserts at end of list
                const prev = exprGrp.tmpls[tplIdx].parentElement?.children[iter + 1];
                // Use the mapped node. If node nd is not rendered, it will use the comment node
                // instead, allowing for future replacement if it becomes rendered.
                // Optional check is needed, there's no mapped nodes if the template root element
                // is not reactive.
                const ndToInsert = localScope.nodes.get(sourceNd)?.nds[iter] || nd;
                if (prev && exprGrp.scopeId && prev.classList.contains(exprGrp.scopeId))
                    prev.replaceWith(ndToInsert);
                else
                    previousSibling.after(ndToInsert);
                previousSibling = ndToInsert;
            }
            // remove old nodes if new list is shorter than the previous one.
            for (let i = oldLength - list.length; i > 0; i--) {
                const parent = exprGrp.tmpls[tplIdx].parentElement;
                if (parent != null && parent.children[list.length + 1] != null)
                    parent.children[list.length + 1].remove();
            }
            // resize mapped node arrays if needed, to trim old nodes
            const scoped = localScope.nodes.get(sourceNd);
            if (scoped && scoped.nds.length > list.length) {
                for (const { nds } of localScope.nodes.values()) {
                    nds.length = list.length;
                }
            }
        }
    }
    /**
     * Template x-if/else rendering: applies the template's x-if to each child,
     * merging with any child x-if/x-else expression as needed.
     * Then clones template children and inserts them into the DOM.
     *
     * @param tmpl - The template element to process.
     * @param scopeId - Optional scope identifier for scoped expressions
     * @param context - Optional scope context containing iteration variables
     */
    function renderTemplate(tmpl, scopeId, context) {
        const frag = document.createDocumentFragment();
        const xif = tmpl.getAttribute(DIRECTIVES.IF);
        // template without x-if or x-else makes no sense, ignore it.
        if (!xif && !tmpl.hasAttribute(DIRECTIVES.ELSE)) {
            console.warn('Ignore useless template'); // eslint-disable-line no-console
            return;
        }
        // if template has x-if, use it as is.
        // if template uses x-else, use the invert of the previous sibling's x-if expression.
        const tmplExpr = xif || `!(${tmpl.previousElementSibling.getAttribute(DIRECTIVES.IF)})`;
        // Apply template x-if to each child,
        // merging with x-if/else expression if child has one
        for (let i = 0; i < tmpl.content.childElementCount; i++) {
            const child = tmpl.content.children[i]; // template child
            const nd = child.cloneNode(true); // cloned version (inserted in DOM)
            let ndif = ''; // child element's new x-if expression
            if (child.hasAttribute(DIRECTIVES.IF)) {
                const childIf = child.getAttribute(DIRECTIVES.IF);
                ndif = `(${tmplExpr}) && (${childIf})`;
            }
            else if (child.hasAttribute(DIRECTIVES.ELSE)) {
                // if an element has x-else directive, its previous sibling must have an x-if
                const previf = child.previousElementSibling.getAttribute(DIRECTIVES.IF);
                ndif = `(${tmplExpr}) && !(${previf})`; // use the invert of the previous sibling's x-if expression
                nd.removeAttribute(DIRECTIVES.ELSE);
            }
            nd.setAttribute(DIRECTIVES.IF, ndif || tmplExpr);
            frag.appendChild(nd);
            parseNode(nd, scopeId, context);
        }
        if (xif)
            // insert before the template, so that template stays the previous sibling of an eventual x-else
            tmpl.before(frag);
        else {
            // if the previous sibling is a template, remove it after processing the x-else element.
            // NOTE: does not handle when template x-else follows a non-template x-if,
            // nor when non-template x-else follows a template x-if. Should not be a problem.
            if (tmpl.previousElementSibling.tagName === 'TEMPLATE')
                tmpl.previousElementSibling.remove();
            // template not needed after processing, can be replaced by its cloned children
            tmpl.replaceWith(frag);
        }
    }
    function renderNode(nd, scopeId, localScope, sourceNd, listLength) {
        const attrs = nd.getAttributeNames();
        let isReactive = false;
        let isReacIter = 0;
        while (!isReactive && isReacIter < attrs.length) {
            if (attrs[isReacIter++].startsWith('x-'))
                isReactive = true;
        }
        if (isReactive || nd.tagName === 'TEMPLATE') {
            const iterator = localScope[1];
            const scopedNodes = scopes.get(scopeId).nodes;
            // create new array of nodes when watched list is updated and first item is processed.
            if (listLength != null && iterator === 0)
                scopedNodes.set(sourceNd, { nds: new Array(listLength) });
            // if node uses x-if directive, process it first.
            // then we can ignore other directives & children if it is not rendered.
            const ifIdx = attrs.indexOf(DIRECTIVES.IF);
            if (ifIdx > -1) {
                attrs.splice(ifIdx, 1);
                // take ref to node next sibling, in case it is removed from fragment.
                let nextSibling;
                if (nd.nextElementSibling && nd.nextElementSibling.hasAttribute(DIRECTIVES.ELSE))
                    nextSibling = nd.nextElementSibling;
                const insert = render(nd, DIRECTIVES.IF, scopeId, localScope);
                if (nextSibling) {
                    if (insert)
                        nextSibling.remove();
                    else {
                        // remove the x-else is enough to make a standard element visible
                        nextSibling.removeAttribute(DIRECTIVES.ELSE);
                    }
                }
                if (!insert) {
                    if (!scopedNodes.has(sourceNd))
                        scopedNodes.set(sourceNd, { nds: new Array(listLength) });
                    // scopedNodes use the comment node that replaces the element,
                    // and delete the map entry, it was only needed to retrieve the comment
                    scopedNodes.get(sourceNd).nds[iterator] = ifMap.get(nd);
                    ifMap.delete(nd);
                    return; // no need to process node and children
                }
                nd.removeAttribute(DIRECTIVES.IF);
            }
            // process other directives for the node
            for (let i = 0; i < attrs.length; i++) {
                const attr = attrs[i];
                if (!!attr && attr.startsWith('x-')) {
                    render(nd, attr, scopeId, localScope);
                    nd.removeAttribute(attr);
                }
            }
            // Create mapping if required. Happens if a rendered x-if contains reactive children.
            if (!scopedNodes.has(sourceNd))
                scopedNodes.set(sourceNd, { nds: new Array(listLength) });
            // keep node at the correct index for its source node.
            scopedNodes.get(sourceNd).nds[iterator] = nd;
        }
        // process node's children
        const children = [...nd.children]; // get a static ref to children nodes
        for (let i = 0; i < children.length; i++) {
            // process only if node has a parent. Otherwise it means it's been removed.
            if (children[i].parentElement)
                renderNode(children[i], scopeId, localScope, sourceNd.children[i], listLength);
        }
    }
    function render(nd, attr, scopeId, localData) {
        const expr = nd.getAttribute(attr);
        const fn = fnMap.get(expr);
        if (!fn) {
            logger.error(expr || 'Expr not found', new Error('Expression not mapped: '));
            return;
        }
        try {
            const res = fn.call(null, app.data, app.store, ...localData);
            return updateNode(nd, attr, res);
        }
        catch (ex) {
            logger.error(`render error [fn=${expr}]`, ex);
            return false;
        }
    }
    function bindEvents(nd, localScope) {
        for (const evt of SUPPORTED_EVENTS) {
            const attr = '@' + evt;
            if (nd.hasAttribute(attr)) {
                const expr = nd.getAttribute(attr);
                // @ts-expect-error TODO: refactor method to return early if conditions are not met
                const { args } = eventMapping.get(expr);
                const params = [];
                for (const arg of args) {
                    if (localScope && localScope.hasOwnProperty(arg)) {
                        const scopeData = localScope[arg];
                        params.push(scopeData);
                    }
                    else if (arg.startsWith('data'))
                        params.push(app.data);
                    else if (arg.startsWith('store'))
                        params.push(app.store);
                }
                const ndState = domStates.get(nd);
                if (ndState)
                    ndState[evt] = { expr, params };
                else
                    domStates.set(nd, { [evt]: { expr, params } });
                const useCapture = nd.dataset.capture != null && nd.dataset.capture !== 'false';
                nd.addEventListener(evt, (e) => {
                    const state = domStates.get(e.currentTarget);
                    if (!state || !state[e.type]) {
                        logger.log('warn', { message: `no event data for ${e.type}`, level: 'warn' });
                        return;
                    }
                    const { fn } = fnMap.get(state[e.type].expr);
                    const params = state[e.type].params;
                    return fn(e, ...params);
                }, useCapture);
                nd.removeAttribute(attr);
            }
        }
        for (let i = 0; i < nd.childElementCount; i++) {
            bindEvents(nd.children[i], localScope);
        }
    }
    function updateNode(nd, rule, result) {
        let insert = true;
        switch (rule) {
            case DIRECTIVES.SHOW:
                if (nd.nodeType !== Node.COMMENT_NODE && nd.dataset.transition != null && !result) {
                    setTimeout(() => { nd.classList.toggle('hidden', !result); }, 400);
                }
                else if (nd.nodeType !== Node.COMMENT_NODE)
                    nd.classList.toggle('hidden', !result);
                break;
            case DIRECTIVES.TEXT:
                nd.textContent = result;
                break;
            case DIRECTIVES.ELSE:
                // no break, use x-if logic with flipped result
                result = !result;
            case DIRECTIVES.IF:
                if (!result) {
                    insert = false;
                    // An  update can be triggered when value does not change (when object changes but not the used property, e.g. array reassign with same length).
                    // The node can already be commented, so we first look in ifMap before creating a new comment.
                    // If we create a new comment each time, we lose reference to the inserted comment when overwriting ifmap.
                    const comm = ifMap.get(nd) || document.createComment('');
                    // insert comment in place of node
                    if (nd.parentElement)
                        nd.replaceWith(comm);
                    // necessary so that renderNode can retrieve the comment node and reference it in scoppedNodes
                    ifMap.set(nd, comm);
                }
                else {
                    // re-insert node
                    const comNd = ifMap.get(nd);
                    if (comNd) {
                        comNd.replaceWith(nd);
                        ifMap.delete(nd);
                    }
                }
                break;
            case DIRECTIVES.CLASS:
                Object.entries(result).forEach(([className, isActive]) => {
                    nd.classList.toggle(className, !!isActive);
                });
                break;
            default: {
                // any attribute can be dynamic. Set attr only on actual change.
                const attr = rule.substring(2);
                if (result != nd.getAttribute(attr))
                    nd.setAttribute(attr, result);
            }
        }
        return insert;
    }
    // updates all DOM nodes that reference the given property
    function renderProperty(propName, oldValue) {
        const propMapping = mapping.get(propName);
        // update the nodes that depend on this property
        if (propMapping) {
            for (const exprGrp of propMapping) {
                if (exprGrp.listPath) {
                    processLoop(exprGrp, oldValue.length);
                }
                else if (exprGrp.scopeId) {
                    // run expr update on all scoped nodes.
                    const localScope = scopes.get(exprGrp.scopeId);
                    // expression can be used on multiple nodes, process them all.
                    for (let j = 0; j < exprGrp.nds.length; j++) {
                        const { nd: tmplNd, rule } = exprGrp.nds[j];
                        const tmplNdData = localScope.nodes.get(tmplNd);
                        // there may be no node to update (e.g. no avails).
                        if (!tmplNdData)
                            continue;
                        const mappedNodes = tmplNdData.nds;
                        const fn = fnMap.get(exprGrp.expr);
                        // process each iteration of the looped list
                        for (let i = 0; i < localScope.items.length; i++) {
                            // compute function for current iteration
                            const res = fn.call(null, app.data, app.store, localScope.items[i], i);
                            // x-if directives require instantiating or removing a node.
                            // other rules just update an attribute value or text content.
                            if (rule === DIRECTIVES.IF) {
                                // switch back to node if it was replaced by a comment.
                                if (res) {
                                    const comm = mappedNodes[i];
                                    let nd = ifMap.get(comm);
                                    if (nd) {
                                        // node exists: clean map and reassign it in mapped node list
                                        ifMap.delete(comm); // no need for comment -> nd entry anymore
                                        mappedNodes[i] = nd;
                                    }
                                    else {
                                        // no ref to actual node: create it. Assignment in mapped node list is done in renderNode.
                                        nd = tmplNd.cloneNode(true);
                                        renderNode(nd, exprGrp.scopeId, [localScope.items[i], i], tmplNd, null);
                                        bindEvents(nd, {
                                            [localScope.itemName]: localScope.items[i],
                                            [localScope.iterName]: i,
                                        });
                                    }
                                    comm.replaceWith(nd);
                                }
                                // condition is false and node exists: replace with comment if not one already.
                                // a property change does not necessarily changes the expression result,replacing
                                // a comment with a comment loses the reference to the original rendered node.
                                else if (!res && mappedNodes[i]?.nodeType !== Node.COMMENT_NODE) {
                                    const comm = document.createComment('');
                                    mappedNodes[i].replaceWith(comm);
                                    ifMap.set(comm, mappedNodes[i]); // keep track of replaced node for later re-insertion
                                    mappedNodes[i] = comm; // reference the comment in nodelist
                                }
                            }
                            else if (mappedNodes[i]) {
                                updateNode(mappedNodes[i], rule, res);
                            }
                        }
                    }
                }
                else {
                    const fn = fnMap.get(exprGrp.expr);
                    const res = fn.call(null, app.data, app.store);
                    // update each node according to expression results
                    // & its associated rule.
                    for (const elem of exprGrp.nds) {
                        updateNode(elem.nd, elem.rule, res);
                    }
                }
            }
        }
        // process watchers
        const fns = watchers.get(propName);
        if (fns) {
            for (let i = 0; i < fns.length; i++) {
                fns[i].call(null, app.store[propName], oldValue);
            }
        }
    }
    /** Proxy handler  */
    const handler = {
        // setter interception
        set: function (obj, prop, value) {
            if (obj[prop] === value)
                return true;
            if (app.data.env === 'dev')
                performance.mark('proxy-set-start');
            const old = obj[prop];
            obj[prop] = value; // set value
            renderProperty(prop, old);
            if (app.data.env === 'dev') {
                performance.measure('proxy-set-end', 'proxy-set-start');
                // @ts-expect-error measure is made above
                const duration = performance.getEntriesByName('proxy-set-end', 'measure').pop().duration;
                console.debug(`proxy set [${prop}]: ${duration.toFixed(1)}ms`); // eslint-disable-line no-console
            }
            return true;
        },
    };
    // public interface
    return {
        start,
        watch,
        // expose internal state for debug
        mapping,
        scopes,
        eventMapping,
        domStates,
        fnMap,
        watchers,
        ifMap,
    };
})();
